import React, { Component } from 'react';
import { ToastContainer, Flip } from 'react-toastify';
import { connect } from 'react-redux';
import NavMenu from './NavMenu';
import 'react-toastify/dist/ReactToastify.css';
import CommonContext, { ApiRoutes } from './Common';
import authService from './api-authorization/AuthorizeService';
import { util } from './Util';
import { ApplyBranding } from './ApplyBranding';
import { HistoryWatcher } from './common/layout/HistoryWatcher';
import { Progress } from 'reactstrap';
import { getIsAuthenticated } from '@s20/selectors/features/common.selectors';
import { getFormIsOpen, getTenant, getUser, getTenantSettings, getTenantTheme } from '@s20/selectors/features/common.selectors';
import { getPermissionStrings } from '@s20/selectors/features/permissions.selectors';
import {
    setFormIsOpen, setIsAuthenticated, setTenant, setUser,
    setPageTitle, setTenantSettings, setTenantTheme, setTimesheetNotifications,
} from '@s20/slices/features/common.slice';

class AppLayout extends Component {
    static displayName = AppLayout.name;

    constructor(props) {
        super(props);
        
        this.setFormOpened = this.setFormOpened.bind(this);
        this.setPageTitle = this.setPageTitle.bind(this);
    }

    componentDidMount() {
        this._subscription = authService.subscribe(() => this.populateState());
        this.populateState();
    }

    componentWillUnmount() {
        authService.unsubscribe(this._subscription);
    }

    checkUserHasPermission = (permission) => {
        // 2022-09-26 - M. Nicol - Refactored to reduce number of operations.
        const { permissions } = this.props;
    
        // Example: location.view -> ["location", "view"] -> category: location, action: view
        const [category, action] = (permission ?? '').split('.');
    
        if (!permissions || !category || !action) return false;
    
        const hasPerm = permissions.some(p => {
            const [p_permission, p_action] = (p || '').split('.');
            return p_permission === category && p_action === action;
        });
        return hasPerm;
    };

    setFormOpened = (value) => {
        const { dispatch } = this.props;

        dispatch(setFormIsOpen(value));
    };

    setPageTitle = (value) => {
        const { dispatch } = this.props;

        dispatch(setPageTitle(value));
    };

    populateState = async () => {
        const { dispatch } = this.props;
        const isAuthenticated = await authService.isAuthenticated();

        // if logged in, get the user details,
        // hydrate the context
        if (isAuthenticated) {
            const [tenant, userProfile, selfDispatchTimesheetNotifications] = await Promise.all([
                util.fetch.js(ApiRoutes.tenant.settings()),
                util.fetch.js(ApiRoutes.auth.profile()),
                util.fetch.js(ApiRoutes.selfDispatchTimesheet.selfDispatchNotifications()),
            ]);

            userProfile.isAdmin = (userProfile.roles ?? [])
                .map((x) => x.name)
                .includes('Administrator');

            dispatch(setIsAuthenticated(isAuthenticated));
            dispatch(setUser(userProfile));
            dispatch(setFormIsOpen(false));
            dispatch(setTenant(tenant));
            dispatch(setTenantSettings(tenant?.tenantSettings));
            dispatch(setTenantTheme(tenant?.tenantSettings?.defaultTheme));

            // TODO - This should be reactive and not just load on page load
            dispatch(setTimesheetNotifications(selfDispatchTimesheetNotifications?.count));
        } else {
            dispatch(setIsAuthenticated(false));
        }
    };

    render() {
        const { tenantSettings, tenantTheme, user, isAuthenticated } = this.props;

        if (
            (!user && isAuthenticated)
            || isAuthenticated === null
            || (isAuthenticated && !tenantSettings)
        ) {
            return <Progress />;
        }

        return (
            <CommonContext.Provider
                value={{
                    tenant: this.props.tenant,
                    permissions: this.props.permissions,
                    formIsOpen: this.props.formIsOpen,
                    version: this.props.version,
                    pageTitle: this.props.pageTitle,
                    setPageTitle: this.setPageTitle,
                    setFormOpened: this.setFormOpened,
                    checkUserHasPermission: this.checkUserHasPermission,
                    user,
                    isAuthenticated,
                }}
            >
                {tenantTheme && (
                    <ApplyBranding
                        colors={{
                            primary: tenantTheme.primaryColor,
                            secondary: tenantTheme.secondaryColor,
                            success: tenantTheme.successColor,
                            warning: tenantTheme.warningColor,
                            danger: tenantTheme.dangerColor,
                            information: tenantTheme.infoColor,
                            defaultHighlight: tenantTheme.defaultHighlightColor,
                            defaultHighlightAccent: tenantTheme.defaultHighlightAccentColor,
                        }}
                    />
                )}
                
                <NavMenu />

                <main id="main" className="container-fluid p-0">
                    {this.props.children}
                </main>

                <ToastContainer
                    position="top-center"
                    transition={Flip}
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="colored"
                    icon={false}
                />

                <HistoryWatcher />
            </CommonContext.Provider>
        );
    }
}

const mapStateToProps = (state) => ({
    isAuthenticated: getIsAuthenticated(state),
    formIsOpen: getFormIsOpen(state),
    user: getUser(state),
    tenant: getTenant(state),
    permissions: getPermissionStrings(state),
    tenantSettings: getTenantSettings(state),
    tenantTheme: getTenantTheme(state),
});

export const Layout = connect(mapStateToProps)(AppLayout);